import React, { useEffect, useRef } from 'react';
import { StaticImage } from 'gatsby-plugin-image';
import styled from 'styled-components';
import { srConfig } from '@config';
import sr from '@utils/sr';
import { usePrefersReducedMotion } from '@hooks';

const StyledAboutSection = styled.section`
  max-width: 900px;

  .inner {
    display: grid;
    grid-template-columns: 3fr 2fr;
    grid-gap: 50px;

    @media (max-width: 768px) {
      display: block;
    }
  }
`;
const StyledText = styled.div`
  ul.skills-list {
    display: grid;
    grid-template-columns: repeat(2, minmax(140px, 200px));
    grid-gap: 0 10px;
    padding: 0;
    margin: 20px 0 0 0;
    overflow: hidden;
    list-style: none;

    li {
      position: relative;
      margin-bottom: 10px;
      padding-left: 20px;
      font-family: var(--font-mono);
      font-size: var(--fz-xs);

      &:before {
        content: '▹';
        position: absolute;
        left: 0;
        color: var(--green);
        font-size: var(--fz-sm);
        line-height: 12px;
      }
    }
  }
`;
const StyledPic = styled.div`
  position: relative;
  max-width: 300px;

  @media (max-width: 768px) {
    margin: 50px auto 0;
    width: 70%;
  }

  .wrapper {
    ${({ theme }) => theme.mixins.boxShadow};
    display: block;
    position: relative;
    width: 100%;
    border-radius: var(--border-radius);
    background-color: var(--green);

    &:hover,
    &:focus {
      outline: 0;
      transform: translate(-4px, -4px);

      &:after {
        transform: translate(8px, 8px);
      }

      .img {
        filter: none;
        mix-blend-mode: normal;
      }
    }

    .img {
      position: relative;
      border-radius: var(--border-radius);
      mix-blend-mode: multiply;
      filter: grayscale(100%) contrast(1);
      transition: var(--transition);
    }

    &:before,
    &:after {
      content: '';
      display: block;
      position: absolute;
      width: 100%;
      height: 100%;
      border-radius: var(--border-radius);
      transition: var(--transition);
    }

    &:before {
      top: 0;
      left: 0;
      background-color: var(--navy);
      mix-blend-mode: screen;
    }

    &:after {
      border: 2px solid var(--green);
      top: 14px;
      left: 14px;
      z-index: -1;
    }
  }
`;

const About = () => {
  const revealContainer = useRef(null);
  const prefersReducedMotion = usePrefersReducedMotion();

  useEffect(() => {
    if (prefersReducedMotion) {
      return;
    }

    sr.reveal(revealContainer.current, srConfig());
  }, []);

  const skills = [
    'Hardware Product Management',
    'Battery & EV Systems',
    'Vehicle NPI (EVT → MP)',
    'Regulatory & Certification',
    'COGS & Value Engineering',
    'Supplier Coordination',
    'Cross-functional Leadership',
    'Mechatronics',
  ];

  return (
    <StyledAboutSection id="about" ref={revealContainer}>
      <h2 className="numbered-heading">About Me</h2>

      <div className="inner">
        <StyledText>
          <div>
            <p>Hi, I'm Lumbi.</p>
            <p>
              I lead hardware product development for EV, battery, and climate-tech companies. Six
              years across mechatronics engineering and product management &mdash; most recently
              taking Sub-Saharan Africa's first locally designed electric motorcycle (the Roam Air)
              from concept to 2,500 units in production, including a 53% COGS reduction that took
              the product from loss-making to break-even.
            </p>
            <p>
              My career has wound through some demanding programmes: full NPI cycles across two
              electric vehicles at <a href="https://www.roam-electric.com/">Roam Electric</a>, the
              deployment of Africa's first Direct Air Capture pilot plant at{' '}
              <a href="https://www.sirona.tech/">Sirona Technologies</a>, and now the full hardware
              portfolio at <a href="https://www.kofa.co/">Kofa</a> &mdash; batteries, swap stations,
              two-wheel vehicle platforms &mdash; across Kenya and Ghana. I started my career on
              heavy industrial infrastructure: pipelines at{' '}
              <a href="https://www.kpc.co.ke/">Kenya Pipeline Company</a>, and HV substations at{' '}
              <a href="https://www.ketraco.co.ke/">Kenya Electricity Transmission Company</a>.
            </p>
            <p>
              I'm at my best when the product is hard, the constraints are real, and the user's
              needs are unforgiving &mdash; which describes nearly everything I've shipped in
              Sub-Saharan Africa.
            </p>
          </div>

          <ul className="skills-list">
            {skills && skills.map((skill, i) => <li key={i}>{skill}</li>)}
          </ul>
        </StyledText>

        <StyledPic>
          <div className="wrapper">
            <StaticImage
              className="img"
              src="../../images/me.jpg"
              width={500}
              quality={95}
              formats={['AUTO', 'WEBP', 'AVIF']}
              alt="Headshot"
            />
          </div>
        </StyledPic>
      </div>
    </StyledAboutSection>
  );
};

export default About;
